import { IFormDescriptorItem } from 'types/Analytics';

import DateHelpers from 'helpers/Date';
import DateFormats from 'constants/dateFormats';

const groupByHiddenAdapter = (values) => values.chartType === 'simple_table';

const groupByDateHiddenAdapter = (values) => {
  if (
    'groupByMultiSelect' in values &&
    Array.isArray(values.groupByMultiSelect) &&
    values.groupByMultiSelect.includes('fraudReceivedOn')
  ) {
    return false;
    // eslint-disable-next-line sonarjs/no-duplicated-branches
  } else if ('groupBy' in values && values.groupBy === 'fraudReceivedOn') {
    return false;
  }

  return true;
};

const groupByOnChangeCallback = (values) => {
  const groupByDateHidden = groupByDateHiddenAdapter(values);

  if (groupByDateHidden && 'groupByDate' in values) {
    values.groupByDate = '';
  }

  return values;
};

const FraudReportParams: IFormDescriptorItem[] = [
  {
    id: 'chartName',
    label: 'analytics.editForm.params.chartTitle.label',
    placeholder: 'analytics.editForm.params.chartTitle.placeholder',
    type: 'input',
  },
  {
    id: 'chartData',
    type: 'singleSelect',
    label: 'analytics.editForm.params.chartData.label',
    options: [
      {
        value: 'numberOfFraudOperations',
        label: 'analytics.editForm.params.chartData.fraudNumber.item',
      },
      {
        value: 'fraudAmountUSD',
        label: 'analytics.editForm.params.chartData.fraudAmount.item',
      },
    ],
    conditionalOptions: [
      {
        data: [
          {
            value: 'fraudRatioByNumber',
            label: 'analytics.editForm.params.chartData.fraudRatioNumber.item',
          },
          {
            value: 'fraudRatioByAmount',
            label: 'analytics.editForm.params.chartData.fraudRatioAmount.item',
          },
        ],
        condition: (values) => values.chartType === 'simple_table',
      },
    ],
    onChangeCallback: (values) => {
      if ('groupByDate' in values) {
        values.groupByDate = '';
      }

      if ('groupByMultiSelect' in values) {
        values.groupByMultiSelect = [];
      }

      if ('groupBy' in values) {
        values.groupBy = '';
      }

      return values;
    },
  },
  {
    id: 'chartType',
    type: 'singleSelect',
    label: 'analytics.editForm.params.chartType.label',
    options: [
      {
        value: 'simple_table',
        label: 'analytics.editForm.params.chartType.table.item',
      },
    ],
    conditionalOptions: [
      {
        data: [
          {
            value: 'bar',
            label: 'analytics.editForm.params.chartType.bar.item',
          },
          {
            value: 'horizontal_bar',
            label: 'analytics.editForm.params.chartType.horizontalBar.item',
          },
          {
            value: 'pie chart',
            label: 'analytics.editForm.params.chartType.pie.item',
          },
        ],
        condition: (values) =>
          values.chartData !== 'fraudRatioByNumber' &&
          values.chartData !== 'fraudRatioByAmount',
      },
    ],
    onChangeCallback: (values) => {
      if (
        values.chartType !== 'simple_table' &&
        (values.chartData === 'fraudRatioByNumber' ||
          values.chartData === 'fraudRatioByAmount')
      ) {
        values.chartData = 'numberOfFraudOperations';
      }

      if (
        values.chartType === 'pie chart' &&
        'groupBy' in values &&
        values.groupBy === 'fraudReceivedOn'
      ) {
        values.groupBy = '';
        values.groupByDate = '';

        return values;
      }

      if (values.chartType === 'simple_table') {
        if ('groupBy' in values) {
          values.groupBy = '';
        }
      } else if ('groupByMultiSelect' in values) {
        values.groupByMultiSelect = [];
      }

      return values;
    },
  },
  {
    id: 'groupBy',
    type: 'singleSelect',
    label: 'analytics.editForm.params.groupBy.label',
    renderOptions: {
      hidden: {
        condition: groupByHiddenAdapter,
      },
    },
    options: [
      {
        value: 'cardType',
        label: 'analytics.editForm.params.groupBy.cardType.item',
      },
      {
        value: 'issuerCountry',
        label: 'analytics.editForm.params.groupBy.countryByBIN.item',
      },
      {
        value: 'projectId',
        label: 'analytics.editForm.params.groupBy.projectID.item',
      },
      {
        value: 'brand',
        // eslint-disable-next-line sonarjs/no-duplicate-string
        label: 'analytics.editForm.params.groupBy.fraudBrand.item',
      },
      {
        value: 'fraudType',
        label: 'analytics.editForm.params.groupBy.fraudType.item',
      },
    ],
    conditionalOptions: [
      {
        data: [
          {
            value: 'fraudReceivedOn',
            label: 'analytics.editForm.params.groupBy.fraudPeriod.item',
          },
        ],
        condition: (values) => values.chartType !== 'pie chart',
      },
    ],
    onChangeCallback: groupByOnChangeCallback,
  },
  {
    id: 'groupByMultiSelect',
    type: 'multiSelect',
    label: 'analytics.editForm.params.groupBy.label',
    options: [
      {
        id: 'cardType',
        text: 'analytics.editForm.params.groupBy.cardType.item',
      },
      {
        id: 'issuerCountry',
        text: 'analytics.editForm.params.groupBy.countryByBIN.item',
      },
      {
        id: 'projectId',
        text: 'analytics.editForm.params.groupBy.projectID.item',
      },
      {
        id: 'brand',
        text: 'analytics.editForm.params.groupBy.fraudBrand.item',
      },
    ],
    conditionalOptions: [
      {
        data: [
          {
            id: 'accountNumber',
            text: 'analytics.editForm.params.groupBy.fraudCard.item',
          },
          {
            id: 'customerId',
            text: 'analytics.editForm.params.groupBy.fraudCustomer.item',
          },
          {
            id: 'fraudType',
            text: 'analytics.editForm.params.groupBy.fraudType.item',
          },
        ],
        condition: (values) =>
          values.chartData !== 'fraudRatioByNumber' &&
          values.chartData !== 'fraudRatioByAmount',
      },
      {
        data: [
          {
            id: 'fraudReceivedOn',
            text: 'analytics.editForm.params.groupBy.fraudPeriod.item',
          },
        ],
        condition: (values) => values.chartType !== 'pie chart',
      },
    ],
    renderOptions: {
      hidden: {
        condition: (values) => values.chartType !== 'simple_table',
      },
    },
    onChangeCallback: groupByOnChangeCallback,
  },
  {
    id: 'groupByDate',
    type: 'singleSelect',
    label: 'analytics.editForm.params.groupPeriodBy.label',
    options: [
      {
        value: '1d',
        label: 'analytics.editForm.params.groupPeriodBy.day.item',
        order: 2,
      },
      {
        value: '1w',
        label: 'analytics.editForm.params.groupPeriodBy.week.item',
        order: 3,
      },
      {
        value: '1m',
        label: 'analytics.editForm.params.groupPeriodBy.month.item',
        order: 4,
      },
      {
        value: '3m',
        label: 'analytics.editForm.params.groupPeriodBy.quarter.item',
        order: 5,
      },
    ],
    renderOptions: {
      hidden: {
        condition: groupByDateHiddenAdapter,
      },
    },
  },
];

const FraudReportFilters: IFormDescriptorItem[] = [
  {
    id: 'period',
    type: 'date',
    label: 'analytics.editForm.params.period.label',
    renderOptions: {
      minDate: DateHelpers.createDate('01.01.2021', 'date').format(
        DateFormats.datetime
      ),
    },
  },
  {
    id: 'projectId',
    type: 'multiSelect',
    label: 'analytics.editForm.filters.project.label',
    dictionaryKey: 'projects',
  },
  {
    id: 'cardType',
    type: 'multiSelect',
    label: 'analytics.editForm.filters.cardType.label',
    options: [
      {
        id: 'Visa',
        text: 'Visa',
      },
      {
        id: 'Mastercard',
        text: 'MasterCard',
      },
    ],
  },
  {
    id: 'issuerCountry',
    type: 'multiSelect',
    label: 'analytics.editForm.filters.countryByBIN.label',
    dictionaryKey: 'country',
  },
  {
    id: 'brandId',
    type: 'multiSelect',
    label: 'analytics.editForm.params.groupBy.fraudBrand.item',
    dictionaryKey: 'analyticsBrand',
  },
];

export { FraudReportParams, FraudReportFilters, groupByHiddenAdapter };
